import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles.module.scss';
import { FormData, ConsentState } from './types';
import { useSliderNavigation } from './hooks';
import SuccessSlide from './components/SuccessSlide';
import {
  DEFAULT_OPTIONAL_CONSENTS,
  OptionalConsentAnswers,
} from '../../shared/consent/OptionalConsents';
import { submitLead, updateConsent } from './utils/api';
import {
  trackSlideAnswer,
  trackSliderSuccess,
  trackSliderFailure,
  trackLeadCreated,
  trackEnpalSourceCookie,
} from './utils/tracking';
import { getUTMParams, isValidEmail, isValidPhone, splitFullName } from './utils/helpers';
import {
  bonificoBand,
  bonificoOf,
  formatEur,
  formatRange,
  quote,
  zoneFromCap,
  zoneMax,
} from './config/valuation';

const PRIVACY_URL = 'https://www.enpal.com/it/protezione-dei-dati';

const SIZE_TO_API: Record<string, string> = {
  '1': 'fino a 110',
  '2': '110-170',
  '3': '170-230',
};

const WHEN_TO_API: Record<string, string> = {
  '1': 'one_to_two_months',
  '2': 'three_to_four_months',
  '3': 'more_than_5_months',
};

const SIZE_LABELS = [
  { value: '1', label: '0 - 110 m²' },
  { value: '2', label: '111 - 180 m²' },
  { value: '3', label: '181+ m²' },
];

const SliderNoicompriamo: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({ product_type: 'Heatpump', payout: 'trade' });
  const [formValues, setFormValues] = useState<Record<string, string>>({ zip: '', name: '', email: '', phone: '' });
  const [boiler, setBoiler] = useState('');
  const [size, setSize] = useState('');
  const [when, setWhen] = useState('');
  const [fv, setFv] = useState('');
  const [focusField, setFocusField] = useState('');
  const [leadUid, setLeadUid] = useState('');
  const seen = useRef(new Set<string>());

  const [optionalConsents, setOptionalConsents] = useState<OptionalConsentAnswers>(
    DEFAULT_OPTIONAL_CONSENTS
  );

  const consentForApi: ConsentState = useMemo(
    () => ({
      enpalMarketingConsent: optionalConsents.enpalMarketingConsent === true,
      thirdPartyMarketing: optionalConsents.thirdPartyMarketing === true,
      thirdPartyProfiling: optionalConsents.thirdPartyProfiling === true,
    }),
    [optionalConsents]
  );

  const { currentSlide, goNext, goPrev, goToSlideById, canGoPrev } = useSliderNavigation(formData);

  const cap = (formValues.zip || '').replace(/\D/g, '').slice(0, 5);
  const zone = zoneFromCap(cap);
  const sizeApi = SIZE_TO_API[size];
  const valuation = zone ? quote(zone, formData.heating_source, sizeApi) : null;

  const remember = useCallback((id: string, title: string, value: string) => {
    const key = id + ':' + value;
    if (seen.current.has(key)) return;
    seen.current.add(key);
    trackSlideAnswer(id, title, value);
  }, []);

  useEffect(() => {
    const utmParams = getUTMParams();
    trackEnpalSourceCookie(utmParams.utm_source || 'organic');
  }, []);

  const updateFormData = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setCap = (next: string) => {
    const digits = next.replace(/\D/g, '').slice(0, 5);
    setFormValues((prev) => ({ ...prev, zip: digits }));
    updateFormData('zip', digits);
    const nextZone = zoneFromCap(digits);
    if (nextZone) remember('location_form', 'Qual è il tuo CAP?', digits);
  };

  const pickBoiler = (value: string) => {
    setBoiler(value);
    remember('boiler_type', 'Che caldaia hai?', value);
  };

  const pickSize = (value: string) => {
    setSize(value);
    const apiValue = SIZE_TO_API[value];
    if (apiValue) updateFormData('house_squared_meters', apiValue);
    remember('house_size', 'Quanto è grande la tua casa?', apiValue || value);
  };

  const pickHeat = (value: string) => {
    updateFormData('heating_source', value);
    remember('heating_system', 'Come riscaldi casa tua?', value);
  };

  const pickWhen = (value: string) => {
    setWhen(value);
    const apiValue = WHEN_TO_API[value];
    if (apiValue) updateFormData('purchase_time', apiValue);
    remember('install_timeline', 'Entro quando vorresti sostituirla?', apiValue || value);
  };

  const pickFv = (value: string) => {
    setFv(value);
    remember('fotovoltaico', 'Hai già il fotovoltaico?', value);
  };

  const pickPayout = (value: string) => {
    updateFormData('payout', value);
    remember('payout', "Come vuoi ricevere l'importo", value);
  };

  const okName = formValues.name.trim().replace(/\s+/g, ' ').length > 2;
  const okMail = isValidEmail(formValues.email || '');
  const phoneDigits = (formValues.phone || '').replace(/\D/g, '');
  const okPhone = isValidPhone(phoneDigits);

  const screenId = currentSlide?.id;
  const screen1Ready = Boolean(zone && boiler && sizeApi);
  const screen2Ready = Boolean(formData.heating_source && when && fv);
  const screen3Ready = okName && okMail && okPhone;

  const handleLeadSubmit = useCallback(async () => {
    goToSlideById('loading');
    const uid = uuidv4();
    setLeadUid(uid);
    const finalFormData: FormData = {
      ...formData,
      ...formValues,
      phone: phoneDigits,
      product_type: 'Heatpump',
    };

    try {
      const id = await submitLead(finalFormData, uid, consentForApi);
      if (id) {
        trackSliderSuccess();
        if (finalFormData.email) {
          const { firstName, lastName } = splitFullName(finalFormData.name || '');
          window.setTimeout(() => {
            trackLeadCreated(
              finalFormData.email!,
              finalFormData.phone || '',
              finalFormData.zip || '',
              firstName,
              lastName
            );
          }, 120);
        }
        goToSlideById('success');
      } else {
        trackSliderFailure();
        goToSlideById('error');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      trackSliderFailure();
      goToSlideById('error');
    }
  }, [formData, formValues, phoneDigits, consentForApi, goToSlideById]);

  const handleConsentsUpdate = useCallback(
    (next: ConsentState) => {
      setOptionalConsents({
        enpalMarketingConsent: next.enpalMarketingConsent,
        thirdPartyMarketing: next.thirdPartyMarketing,
        thirdPartyProfiling: next.thirdPartyProfiling,
      });
      if (leadUid) updateConsent(leadUid, next);
    },
    [leadUid]
  );

  const onCta = () => {
    if (screenId === 'casa' && screen1Ready) goNext();
    else if (screenId === 'impianto' && screen2Ready) goNext();
    else if (screenId === 'contatti' && screen3Ready) handleLeadSubmit();
  };

  const ctaDisabled =
    screenId === 'casa' ? !screen1Ready : screenId === 'impianto' ? !screen2Ready : !screen3Ready;

  const ctaCopy = ctaContent(screenId, zone, sizeApi, formData.heating_source, formData.payout, valuation);

  const showWizard = screenId === 'casa' || screenId === 'impianto' || screenId === 'contatti';
  const stepOn = screenId === 'casa' ? 1 : screenId === 'impianto' ? 2 : screenId === 'contatti' ? 3 : 0;

  const firstInvalid = !okName ? 'name' : !okMail ? 'email' : !okPhone ? 'phone' : '';
  const fieldState = (key: string, valid: boolean) => {
    if (focusField === key) return styles.live;
    if (valid) return styles.done;
    return firstInvalid === key ? styles.live : styles.off;
  };

  return (
    <section className={styles.sliderSection}>
      <div className={styles.shell}>
        <header className={styles.top}>
          <div className={styles.head}>
            <span className={styles.brand} role="img" aria-label="noicompriamocaldaie.it">
              noi<b>compriamo</b>caldaie<b>.it</b>
            </span>
            <span className={styles.by}>
              by <em>Enpal</em>
            </span>
          </div>
          {showWizard && (
            <>
              <h1 className={styles.ttl}>
                {screenId === 'casa' && (
                  <>
                    Quanto vale <span>la tua vecchia caldaia?</span>
                  </>
                )}
                {screenId === 'impianto' && (
                  <>
                    Ancora <span>tre cose</span>
                  </>
                )}
                {screenId === 'contatti' && (
                  <>
                    A chi intestiamo <span>la valutazione?</span>
                  </>
                )}
              </h1>
              <p className={styles.sub}>
                {screenId === 'casa' && (
                  <>
                    Valutazione gratuita · fino a <b>{formatEur(zone ? zoneMax(zone) : zoneMax('F'))}</b>
                  </>
                )}
                {screenId === 'impianto' && 'Tre risposte e ci siamo.'}
                {screenId === 'contatti' && 'Te la confermiamo per iscritto in 24 ore.'}
              </p>
              <div className={styles.steps} aria-hidden="true">
                <i className={stepOn >= 1 ? styles.on : ''} />
                <i className={stepOn >= 2 ? styles.on : ''} />
                <i className={stepOn >= 3 ? styles.on : ''} />
              </div>
            </>
          )}
        </header>

        <div className={styles.body}>
          {screenId === 'casa' && (
            <>
              <div className={blockClass(zone ? 'done' : 'live')}>
                <p className={styles.qt}>Qual è il tuo CAP?</p>
                <CapBoxes value={cap} onChange={setCap} />
                <p className={styles.capError}>
                  {cap.length === 5 && !zone ? 'Questo CAP non risulta in elenco.' : ''}
                </p>
              </div>
              <div className={blockClass(!zone ? 'off' : boiler ? 'done' : 'live')}>
                <p className={styles.qt}>Che caldaia hai?</p>
                <div className={`${styles.row} ${styles.wide2}`}>
                  <Option label="Gas" selected={boiler === 'gas'} onClick={() => pickBoiler('gas')} />
                  <Option label="Gasolio o GPL" selected={boiler === 'gasolio'} onClick={() => pickBoiler('gasolio')} />
                  <Option label="Altro" muted selected={boiler === 'altro'} onClick={() => pickBoiler('altro')} />
                </div>
              </div>
              <div className={blockClass(!boiler ? 'off' : size ? 'done' : 'live')}>
                <p className={styles.qt}>Quanto è grande la tua casa?</p>
                <div className={styles.plan}>
                  {SIZE_LABELS.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      className={`${styles.pl} ${size === item.value ? styles.sel : ''}`}
                      onClick={() => pickSize(item.value)}
                    >
                      <HouseIcon index={Number(item.value) - 1} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {screenId === 'impianto' && (
            <>
              {canGoPrev && (
                <button type="button" className={styles.backLink} onClick={goPrev}>
                  ← Indietro
                </button>
              )}
              <div className={blockClass(formData.heating_source ? 'done' : 'live')}>
                <p className={styles.qt}>Come riscaldi casa tua?</p>
                <div className={styles.row}>
                  <Option label="Termosifoni" selected={formData.heating_source === 'termosifoni'} onClick={() => pickHeat('termosifoni')} />
                  <Option label="Pavimento" selected={formData.heating_source === 'pavimento'} onClick={() => pickHeat('pavimento')} />
                  <Option label="Ventole" selected={formData.heating_source === 'ventole'} onClick={() => pickHeat('ventole')} />
                </div>
              </div>
              <div className={blockClass(!formData.heating_source ? 'off' : when ? 'done' : 'live')}>
                <p className={styles.qt}>Entro quando vorresti sostituirla?</p>
                <div className={styles.row}>
                  <Option label="1-2 mesi" selected={when === '1'} onClick={() => pickWhen('1')} />
                  <Option label="3-6 mesi" selected={when === '2'} onClick={() => pickWhen('2')} />
                  <Option label="Più avanti" selected={when === '3'} onClick={() => pickWhen('3')} />
                </div>
              </div>
              <div className={blockClass(!when ? 'off' : fv ? 'done' : 'live')}>
                <p className={styles.qt}>Hai già il fotovoltaico?</p>
                <div className={`${styles.row} ${styles.fv3}`}>
                  <Option label={"Ce l'ho già"} selected={fv === 'si'} onClick={() => pickFv('si')} />
                  <Option label="No, lo voglio" emphasize selected={fv === 'voglio'} onClick={() => pickFv('voglio')} />
                  <Option label="No, grazie" muted selected={fv === 'no'} onClick={() => pickFv('no')} />
                </div>
              </div>
            </>
          )}

          {screenId === 'contatti' && valuation && valuation.value !== null && valuation.high !== null && (
            <>
              {canGoPrev && (
                <button type="button" className={styles.backLink} onClick={goPrev}>
                  ← Indietro
                </button>
              )}
              <div className={`${styles.fwrap} ${fieldState('name', okName)}`}>
                <p className={styles.fl}>Nome e cognome</p>
                <input
                  className={styles.fi}
                  value={formValues.name}
                  placeholder="Come ti chiami"
                  onFocus={() => setFocusField('name')}
                  onBlur={() => {
                    setFocusField('');
                    if (okName) remember('name_form', 'Nome e cognome', 'continue_button');
                  }}
                  onChange={(event) => setFormValues((prev) => ({ ...prev, name: event.target.value }))}
                />
              </div>
              <div className={`${styles.fwrap} ${fieldState('email', okMail)}`}>
                <p className={styles.fl}>Email</p>
                <input
                  className={styles.fi}
                  type="email"
                  value={formValues.email}
                  placeholder="nome@esempio.it"
                  onFocus={() => setFocusField('email')}
                  onBlur={() => {
                    setFocusField('');
                    if (okMail) remember('email_form', 'Email', 'continue_button');
                  }}
                  onChange={(event) => setFormValues((prev) => ({ ...prev, email: event.target.value }))}
                />
              </div>
              <div className={`${styles.fwrap} ${fieldState('phone', okPhone)}`}>
                <p className={styles.fl}>Cellulare</p>
                <div className={styles.telw}>
                  <span className={styles.pfx}>+39</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    value={formatPhone(phoneDigits)}
                    placeholder="333 1234567"
                    onFocus={() => setFocusField('phone')}
                    onBlur={() => {
                      setFocusField('');
                      if (okPhone) remember('phone_form', 'Cellulare', 'continue_button');
                    }}
                    onChange={(event) =>
                      setFormValues((prev) => ({
                        ...prev,
                        phone: event.target.value.replace(/\D/g, '').slice(0, 15),
                      }))
                    }
                  />
                </div>
              </div>
              <div className={`${styles.tail} ${firstInvalid ? styles.off : ''}`}>
                <p className={styles.fl}>Come vuoi ricevere l&apos;importo?</p>
                <div className={styles.modo2}>
                  <button
                    type="button"
                    className={`${styles.m2} ${styles.best} ${formData.payout === 'trade' ? styles.sel : ''}`}
                    onClick={() => pickPayout('trade')}
                  >
                    <b>Permuta</b>
                    <span>La supervalutiamo e la scontiamo subito sulla nuova pompa di calore. Ritiro e pratiche a carico nostro.</span>
                    <u>{formatRange(valuation.value, valuation.high)}</u>
                  </button>
                  <button
                    type="button"
                    className={`${styles.m2} ${formData.payout === 'wire' ? styles.sel : ''}`}
                    onClick={() => pickPayout('wire')}
                  >
                    <b>Bonifico GSE</b>
                    <span>L&apos;incentivo te lo versa il GSE. Pratiche a carico tuo.</span>
                    <u className={styles.lo}>{formatEur(bonificoOf(valuation.value, valuation.high))}</u>
                  </button>
                </div>
                <label className={`${styles.pcard} ${optionalConsents.thirdPartyMarketing ? styles.on : ''}`}>
                  <span className={styles.tx}>
                    <b>Ricevi anche offerte dai partner</b>
                    <span>
                      Comunichiamo i tuoi dati ai{' '}
                      <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                        partner in elenco
                      </a>
                      , che agiscono come titolari autonomi. Facoltativo, revocabile in ogni momento.
                    </span>
                  </span>
                  <span className={styles.sw}>
                    <input
                      type="checkbox"
                      checked={optionalConsents.thirdPartyMarketing === true}
                      onChange={() =>
                        setOptionalConsents((prev) => ({
                          ...prev,
                          thirdPartyMarketing: prev.thirdPartyMarketing !== true,
                        }))
                      }
                    />
                  </span>
                </label>
              </div>
            </>
          )}

          {screenId === 'loading' && (
            <div className={styles.statusBlock}>
              <h2 className={styles.qt}>Invio della richiesta in corso...</h2>
              <p>Attendere prego...</p>
            </div>
          )}

          {screenId === 'success' && (
            <SuccessSlide consents={consentForApi} onConsentsChange={handleConsentsUpdate} />
          )}

          {screenId === 'error' && (
            <div className={styles.statusBlock}>
              <h2 className={styles.qt}>Qualcosa è andato storto</h2>
              <p>Si è verificato un errore. Riprova tra poco.</p>
              <button type="button" className={styles.retry} onClick={() => goToSlideById('contatti')}>
                Riprova
              </button>
            </div>
          )}
        </div>

        {showWizard && (
          <footer className={styles.foot}>
            <button type="button" className={styles.cta} disabled={ctaDisabled} onClick={onCta}>
              <span className={styles.ctaL}>
                <small>{ctaCopy.lab}</small>
                <b>{ctaCopy.amount}</b>
              </span>
              <span className={styles.ctaAct}>{ctaCopy.action}</span>
            </button>
            <p className={styles.disc}>
              Valore di permuta della caldaia esistente, corrispondente all&apos;incentivo Conto Termico 3.0
              attivabile con la sostituzione. Stima non vincolante, soggetta a verifica GSE e sopralluogo.
            </p>
          </footer>
        )}
      </div>
    </section>
  );
};

function ctaContent(
  screenId: string | undefined,
  zone: ReturnType<typeof zoneFromCap>,
  sizeApi: string | undefined,
  heat: string | undefined,
  payout: string | undefined,
  valuation: ReturnType<typeof quote> | null
): { lab: string; amount: string; action: string } {
  const action =
    screenId === 'impianto'
      ? 'Dove mandiamo la valutazione →'
      : screenId === 'contatti'
        ? 'Blocca il valore →'
        : 'Scopri quanto vale →';

  if (!zone || !valuation) {
    return { lab: 'Inserisci il CAP per iniziare', amount: '—', action };
  }

  const open = (low: number) => formatRange(low, Math.min(low + 1500, zoneMax(zone)));

  if (screenId === 'contatti' && valuation.value !== null && valuation.high !== null) {
    if (payout === 'wire') {
      const band = bonificoBand(valuation.value, valuation.high);
      return { lab: 'Bonifico', amount: formatRange(band.low, band.high), action };
    }
    return { lab: 'Permuta', amount: formatRange(valuation.value, valuation.high), action };
  }

  if (valuation.value !== null) {
    return { lab: 'Valore confermato', amount: open(valuation.value), action };
  }

  if (sizeApi) {
    const sized = quote(zone, 'termosifoni', sizeApi);
    if (sized.value !== null) {
      return { lab: 'Valore garantito finora', amount: open(sized.value), action };
    }
  }

  return { lab: 'Valore garantito finora', amount: open(valuation.c1), action };
}

function blockClass(state: 'off' | 'done' | 'live'): string {
  const tone = state === 'off' ? styles.off : state === 'done' ? styles.done : styles.live;
  return `${styles.blk} ${tone}`;
}

function Option({
  label,
  selected,
  muted,
  emphasize,
  onClick,
}: {
  label: string;
  selected: boolean;
  muted?: boolean;
  emphasize?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.o} ${selected ? styles.sel : ''} ${muted ? styles.muted : ''} ${emphasize ? styles.emphasize : ''}`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function HouseIcon({ index }: { index: number }) {
  if (index === 1) {
    return (
      <svg viewBox="0 0 100 100" width="38" height="38" aria-hidden="true">
        <rect x="12" y="22" width="76" height="56" rx="4" />
        <line x1="12" y1="50" x2="88" y2="50" />
      </svg>
    );
  }
  if (index === 2) {
    return (
      <svg viewBox="0 0 100 100" width="38" height="38" aria-hidden="true">
        <rect x="10" y="18" width="80" height="64" rx="4" />
        <line x1="10" y1="44" x2="62" y2="44" />
        <line x1="62" y1="18" x2="62" y2="82" />
        <line x1="62" y1="56" x2="90" y2="56" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 100 100" width="38" height="38" aria-hidden="true">
      <rect x="18" y="18" width="64" height="64" rx="4" />
    </svg>
  );
}

function formatPhone(digits: string): string {
  if (digits.length > 3) return digits.slice(0, 3) + ' ' + digits.slice(3);
  return digits;
}

function CapBoxes({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const focusAt = (index: number) => {
    refs.current[Math.max(0, Math.min(4, index))]?.focus();
  };

  return (
    <div className={styles.cap}>
      {Array.from({ length: 5 }, (_, index) => (
        <input
          key={index}
          ref={(node) => {
            refs.current[index] = node;
          }}
          className={focusedIndex === index ? styles.digitOn : ''}
          inputMode="numeric"
          maxLength={1}
          aria-label={`Cifra CAP ${index + 1}`}
          value={value[index] || ''}
          onFocus={(event) => {
            setFocusedIndex(index);
            event.currentTarget.select();
          }}
          onChange={(event) => {
            const digit = event.target.value.replace(/\D/g, '').slice(-1);
            if (!digit) return;
            const next = (value.slice(0, index) + digit + value.slice(index + 1)).slice(0, 5);
            onChange(next);
            if (index < 4) focusAt(index + 1);
          }}
          onKeyDown={(event) => {
            if (/^\d$/.test(event.key)) {
              event.preventDefault();
              const next = (value.slice(0, index) + event.key + value.slice(index + 1)).slice(0, 5);
              onChange(next);
              if (index < 4) focusAt(index + 1);
              return;
            }
            if (event.key === 'Backspace') {
              event.preventDefault();
              if (value[index]) {
                onChange(value.slice(0, index) + value.slice(index + 1));
              } else if (index > 0) {
                onChange(value.slice(0, index - 1) + value.slice(index));
                focusAt(index - 1);
              }
            } else if (event.key === 'ArrowLeft') {
              focusAt(index - 1);
            } else if (event.key === 'ArrowRight') {
              focusAt(index + 1);
            }
          }}
          onPaste={(event) => {
            event.preventDefault();
            const text = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
            onChange(text);
            focusAt(Math.min(text.length, 4));
          }}
        />
      ))}
    </div>
  );
}

export default SliderNoicompriamo;
